/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import { ModalScrollScreen } from '../../../shared/layout/ModalScrollScreen'
import { useContentLayout } from '../../../shared/layout/useContentLayout';
import { modalExperience } from '../../../shared/modals/modalExperience.styles';
import { operationalFilterStyles } from '../../../shared/filters/operationalFilters.styles';
import * as Clipboard from 'expo-clipboard';
import { colors } from '../../../shared/theme/colors'
import { ticketColors } from '../../../shared/theme/ticketColors'
import { spacing } from '../../../shared/theme/spacing'
import { typography } from '../../../shared/theme/typography';
import { settingsStyles } from '../../../settings/screens/settingsScreen.styles';
import { useSessionStore } from '../../../shell/session/store/useSessionStore'
import type { BranchPermission, ModuleKey } from '../../../shell/session/store/sessionTypes';
import { usePlanEntitlements } from '../../../shell/providers/PlanEntitlementsProvider';
import { useGlobalFeatureFlags } from '../../../shell/providers/GlobalFeatureFlagsProvider'
import { isGlobalFeatureOn } from '../../../shell/feature-flags/globalFeatureFlagsService'
import { planAllowsCustomer, planAllowsPromotions } from '../../../shell/feature-flags/planEntitlementsService';
import { moduleAllowedByCapabilities } from '../../../shell/feature-flags/businessCapabilitiesService';
import { isBillingEnabled } from '../../../shell/billing/isBillingEnabled';
import { inviteEmployee, invitationUrlFromToken, isActiveEmployeeEmail } from '../../application/employeeInvitations';
import { mapInviteError } from '../../application/mapInviteError';
import { MODULES } from '../../application/employeesApi';
import { useTeamAccess } from '../../application/useTeamAccess';
import { useEmployeesStore, withSelfOwner } from '../../store/useEmployeesStore';
import { ROLE_PRESETS, filterPresetsByCapabilities } from './employeePermissions/EmployeeRolePresets';
import { buildEffectivePermissionSummary } from './employeePermissions/permissionUiLabels';
import {
  branchPermissionsFromPreset,
  branchPermissionsToInvitationPayload,
  permissionsDifferFromPreset,
} from './employeePermissions/permissionPreview';
import { InviteBranchPicker } from './InviteBranchPicker';
import { InvitePermissionCustomizer } from './InvitePermissionCustomizer';
import { useEffectiveBusinessCapabilities } from '../../../shell/session/hooks/useEffectiveBusinessCapabilities';
import { formatLocalDateTime } from '@shared/vueltoDateTime';
import { skipWeekOneTeamStep } from '../../../onboarding/application/onboardingUiStorage';
import { useAuthStore } from '../../../auth/store/useAuthStore';

const DEFAULT_ROLE_ID = 'cashier';

type InviteEmployeeRouteParams = {
  presetRoleId?: string;
  branchIds?: string[];
  weekOneFlow?: boolean;
};

type SuccessState = {
  email: string;
  invitedAt: string;
  token: string;
  inviteUrl: string;
  isExtension: boolean;
};

export const InviteEmployeeScreen = () => {
  const navigation = useNavigation<any>();
  const route = useRoute();
  const routeParams = (route.params ?? {}) as InviteEmployeeRouteParams;
  const weekOneFlow = routeParams.weekOneFlow === true;
  const userId = useAuthStore((s) => s.session?.user?.id);
  const branches = useSessionStore((state) => state.branches);
  const employeesRaw = useEmployeesStore((s) => s.employees);
  const selfOwner = useEmployeesStore((s) => s.selfOwner);
  const employees = useMemo(() => withSelfOwner(employeesRaw, selfOwner), [employeesRaw, selfOwner]);
  const loadEmployees = useEmployeesStore((s) => s.loadEmployees);
  const { layoutWide, layoutWebShell } = useContentLayout('settingsWide');
  const mobile = !layoutWebShell;

  const initialBranchIds = useMemo(() => {
    const fromRoute = routeParams.branchIds?.filter(Boolean) ?? [];
    if (fromRoute.length > 0) return new Set(fromRoute);
    return branches[0]?.id ? new Set([branches[0].id]) : new Set<string>();
  }, [branches, routeParams.branchIds]);

  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [roleId, setRoleId] = useState(routeParams.presetRoleId ?? DEFAULT_ROLE_ID);
  const [selectedBranchIds, setSelectedBranchIds] = useState<Set<string>>(initialBranchIds);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [success, setSuccess] = useState<SuccessState | null>(null);
  const [copyFeedback, setCopyFeedback] = useState<string | undefined>();
  const [customizePermissionsOpen, setCustomizePermissionsOpen] = useState(false);
  const [draftPermissions, setDraftPermissions] = useState<BranchPermission[]>([]);
  const { entitlements, loading: entLoading } = usePlanEntitlements();
  const { flags: globalFeatureFlags } = useGlobalFeatureFlags();
  const { canInvite } = useTeamAccess();
  const canManageTeam = canInvite;
  const effectiveCapabilities = useEffectiveBusinessCapabilities();
  const visiblePresets = useMemo(
    () => filterPresetsByCapabilities(ROLE_PRESETS, effectiveCapabilities),
    [effectiveCapabilities],
  );

  useEffect(() => {
    void loadEmployees();
  }, [loadEmployees]);

  const extensionLikely = useMemo(
    () => isActiveEmployeeEmail(email, employees),
    [email, employees],
  );

  const sortedBranches = useMemo(() => {
    return [...branches].sort((a, b) => a.name.localeCompare(b.name));
  }, [branches]);

  const selectedPreset = useMemo(
    () => ROLE_PRESETS.find((p) => p.id === roleId) ?? ROLE_PRESETS.find((p) => p.id === DEFAULT_ROLE_ID)!,
    [roleId],
  );

  const includeCustomerPermissionRow =
    isGlobalFeatureOn(globalFeatureFlags, 'fiado_integrations') && planAllowsCustomer(entitlements);
  const includePromotionsPermissionRow =
    isGlobalFeatureOn(globalFeatureFlags, 'promotions_integrations') && planAllowsPromotions(entitlements);
  const includeExpensesPermissionRow = isGlobalFeatureOn(globalFeatureFlags, 'expenses_module');
  const includePurchasesPermissionRow = effectiveCapabilities.has('purchases');

  const visibleModules = useMemo((): ModuleKey[] => {
    return MODULES.filter(
      (m) =>
        moduleAllowedByCapabilities(m, effectiveCapabilities) &&
        (includeCustomerPermissionRow || m !== 'Customers') &&
        (includePromotionsPermissionRow || m !== 'Promotions') &&
        (includeExpensesPermissionRow || m !== 'Expenses') &&
        (includePurchasesPermissionRow || m !== 'Purchases')
    );
  }, [
    effectiveCapabilities,
    includeExpensesPermissionRow,
    includeCustomerPermissionRow,
    includePromotionsPermissionRow,
    includePurchasesPermissionRow,
  ]);

  const presetPermissions = useMemo(
    () => branchPermissionsFromPreset(selectedPreset, visibleModules),
    [selectedPreset, visibleModules]
  );

  React.useEffect(() => {
    setDraftPermissions(presetPermissions);
    setCustomizePermissionsOpen(false);
  }, [presetPermissions]);

  const permissionsCustomized = useMemo(
    () => permissionsDifferFromPreset(draftPermissions, presetPermissions),
    [draftPermissions, presetPermissions]
  );

  const rolePreview = useMemo(() => {
    return buildEffectivePermissionSummary(draftPermissions, visibleModules);
  }, [draftPermissions, visibleModules]);

  const userCount = entitlements?.user_count;
  const maxUsers = entitlements?.max_users ?? null;
  // Sin billing / sin tope de plan: no mostrar cupo (el conteo solo confunde).
  const showQuota = isBillingEnabled() && maxUsers != null && typeof userCount === 'number';
  const quotaLabel = showQuota ? `Usás ${userCount} de ${maxUsers} usuarios` : null;
  const quotaFull = showQuota && userCount >= maxUsers;

  const hasBranches = branches.length >= 1;
  const trimmedEmail = email.trim();
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail);
  const canSubmit = hasBranches && selectedBranchIds.size >= 1 && !loading && !quotaFull && emailValid;

  React.useEffect(() => {
    if (!hasBranches) return;
    setSelectedBranchIds((prev) => {
      if (prev.size > 0) return prev;
      const first = branches[0]?.id;
      return first ? new Set([first]) : new Set();
    });
  }, [hasBranches, branches]);

  const handleSubmit = async () => {
    setError(undefined);
    setSuccess(null);
    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail) {
      setError('Ingresá el correo laboral.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setError('Ingresá un correo válido.');
      return;
    }
    if (!hasBranches || selectedBranchIds.size === 0) {
      setError('Seleccioná al menos una sucursal antes de enviar.');
      return;
    }
    if (quotaFull) {
      setError('Alcanzaste el cupo de usuarios de tu plan. Mejorá el plan o liberá invitaciones pendientes.');
      return;
    }
    const branchIdsList = [...selectedBranchIds];
    setLoading(true);
    try {
      const invitation = await inviteEmployee({
        email: trimmedEmail,
        fullName: fullName.trim() || undefined,
        role: selectedPreset.id,
        permissions: branchPermissionsToInvitationPayload(draftPermissions),
        branchIds: branchIdsList,
        branchId: branchIdsList[0],
      });
      const invitedAt = typeof invitation.invited_at === 'string' ? invitation.invited_at : '';
      const token = typeof invitation.token === 'string' ? invitation.token : '';
      const inviteUrl = invitationUrlFromToken(token) ?? '';
      setSuccess({
        email: invitation.email,
        invitedAt,
        token,
        inviteUrl,
        isExtension: invitation.is_extension === true,
      });
      setEmail('');
      setFullName('');
      setRoleId(DEFAULT_ROLE_ID);
    } catch (err: any) {
      setError(
        mapInviteError(err?.message, 'No pudimos enviar la invitación.')
      );
    } finally {
      setLoading(false);
    }
  }

  if (entLoading) {
    return (
      <View style={[styles.container, styles.centered]}>
        <ActivityIndicator size="large" color={ticketColors.gold} />
        <Text style={[styles.subheading, { textAlign: 'center', marginTop: spacing.md }]}>Cargando cuenta…</Text>
      </View>
    );
  }

  if (!canManageTeam) {
    return (
      <ModalScrollScreen variant={mobile ? 'settings' : 'settingsWide'} contentContainerStyle={styles.content}>
        <View style={[settingsStyles.card, styles.deniedBox]}>
          <Text style={settingsStyles.cardTitle}>No disponible</Text>
          <Text style={settingsStyles.caption}>
            {!entitlements
            ? 'No pudimos cargar el plan de tu cuenta. Reintentá en unos segundos.'
            : !canInvite
              ? 'No tenés permiso para invitar empleados.'
              : 'Tu plan no incluye empleados invitados. Mejorá el plan o escribinos a soporte.'}
          </Text>
        </View>
      </ModalScrollScreen>
    );
  }

  return (
    <ModalScrollScreen
      variant={mobile ? 'settings' : 'settingsWide'}
      contentContainerStyle={styles.content}
      keyboardShouldPersistTaps="handled"
    >
      {quotaLabel ? (
        <View
          style={[settingsStyles.banner, quotaFull ? settingsStyles.bannerAccent : settingsStyles.bannerInfo]}
          testID="perfil.team.invite.quota"
        >
          <Text style={settingsStyles.bannerTitle}>{quotaLabel}</Text>
          {quotaFull ? (
            <Text style={settingsStyles.bannerBody}>
              Liberá invitaciones pendientes o mejorá el plan antes de sumar gente nueva.
            </Text>
          ) : (
            <Text style={settingsStyles.bannerBody}>
              Cuando se llena, no podés sumar más hasta liberar un lugar.
            </Text>
          )}
        </View>
      ) : null}

      {weekOneFlow ? (
        <View style={[settingsStyles.banner, settingsStyles.bannerInfo]}>
          <Text style={settingsStyles.bannerBody}>Invitá colaboradores.</Text>
        </View>
      ) : null}

      <View style={modalExperience.formPanel}>
        <View style={layoutWide ? settingsStyles.layoutWideRow : undefined}>
          <View style={layoutWide ? settingsStyles.layoutWideCol : undefined}>
            <View style={styles.fieldGroup}>
              <Text style={modalExperience.formSectionTitle}>Correo laboral *</Text>
              <TextInput
                value={email}
                onChangeText={setEmail}
                placeholder="empleado@mitiendita.com"
                placeholderTextColor={colors.inputPlaceholder}
                autoCapitalize="none"
                keyboardType="email-address"
                style={modalExperience.formInput}
              />
            </View>
          </View>
          <View style={layoutWide ? settingsStyles.layoutWideCol : undefined}>
            <View style={styles.fieldGroup}>
              <Text style={modalExperience.formSectionTitle}>Nombre completo</Text>
              <TextInput
                value={fullName}
                onChangeText={setFullName}
                placeholder="Nombre y apellido"
                placeholderTextColor={colors.inputPlaceholder}
                style={modalExperience.formInput}
              />
            </View>
          </View>
        </View>

        <View style={styles.fieldGroup}>
          <Text style={modalExperience.formSectionTitle}>Rol</Text>
          <Text style={styles.roleHint}>
            Cada rol trae permisos listos. Se aplican cuando acepte. Después los cambiás en Equipo.
          </Text>
          <View style={settingsStyles.chipRow}>
            {visiblePresets.map((preset) => {
              const selected = roleId === preset.id;
              return (
                <TouchableOpacity
                  key={preset.id}
                  style={[
                    operationalFilterStyles.chip,
                    selected && operationalFilterStyles.chipActive,
                  ]}
                  onPress={() => setRoleId(preset.id)}
                  accessibilityLabel={`Rol ${preset.label}`}
                  accessibilityRole="button"
                  accessibilityState={{ selected }}
                >
                  <Text
                    style={[
                      operationalFilterStyles.chipText,
                      selected && operationalFilterStyles.chipTextActive,
                    ]}
                  >
                    {preset.label}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>
          <View style={styles.rolePreviewBox} testID="perfil.team.invite.role-preview">
            <Text style={styles.rolePreviewTitle}>Qué puede hacer</Text>
            <Text style={styles.rolePreviewCan}>Puede: {rolePreview.can}</Text>
            <Text style={styles.rolePreviewCannot}>No puede: {rolePreview.cannot}</Text>
          </View>
          <InvitePermissionCustomizer
            expanded={customizePermissionsOpen}
            onToggleExpanded={() => setCustomizePermissionsOpen((v) => !v)}
            visibleModules={visibleModules}
            permissions={draftPermissions}
            onChange={setDraftPermissions}
            customized={permissionsCustomized}
          />
        </View>

        <View style={styles.fieldGroup}>
          <Text style={modalExperience.formSectionTitle}>Sucursales asignadas</Text>
          <InviteBranchPicker
            branches={sortedBranches}
            selectedIds={selectedBranchIds}
            onChange={setSelectedBranchIds}
            layoutWide={layoutWide}
          />
          {sortedBranches.length === 0 ? <Text style={styles.emptyBranches}>No hay sucursales cargadas.</Text> : null}
        </View>

        {!hasBranches && (
          <Text style={styles.noBranchesMessage}>No hay sucursales cargadas. Agregá una sucursal antes de invitar.</Text>
        )}
      </View>

      <View accessibilityLiveRegion="polite">
        {error ? <Text style={styles.error}>{error}</Text> : null}
      </View>

      {extensionLikely ? (
        <View
          style={[settingsStyles.banner, settingsStyles.bannerInfo]}
          testID="perfil.team.invite.extension-hint"
        >
          <Text style={settingsStyles.bannerTitle}>Esta persona ya está en el equipo</Text>
          <Text style={settingsStyles.bannerBody}>
            Le vamos a sumar las sucursales que elijas. Tiene que aceptar el link con la misma cuenta.
          </Text>
        </View>
      ) : null}

      <TouchableOpacity
        style={[settingsStyles.primaryButton, !canSubmit && settingsStyles.disabled]}
        onPress={handleSubmit}
        disabled={!canSubmit}
        accessibilityLabel={extensionLikely ? 'Sumar sucursales' : 'Enviar invitación'}
        accessibilityRole="button"
      >
        {loading ? (
          <ActivityIndicator color="#FFFFFF" />
        ) : (
          <Text style={settingsStyles.primaryButtonText}>
            {extensionLikely ? 'Sumar sucursales' : 'Enviar invitación'}
          </Text>
        )}
      </TouchableOpacity>

      {weekOneFlow ? (
        <TouchableOpacity
          style={settingsStyles.secondaryButton}
          onPress={() => {
            if (!userId) return;
            void skipWeekOneTeamStep(userId).then(() => {
              if (navigation.canGoBack()) navigation.goBack();
              else navigation.navigate('MainTabs' as never);
            });
          }}
          accessibilityRole="button"
          testID="perfil.team.invite.week-one-skip"
        >
          <Text style={settingsStyles.secondaryButtonText}>Solo yo por ahora</Text>
        </TouchableOpacity>
      ) : null}

      {success ? (
        <View style={styles.successBlock} accessibilityLiveRegion="polite">
          <View style={[settingsStyles.banner, settingsStyles.bannerSuccess]}>
            <Text style={settingsStyles.bannerTitle}>
              {success.isExtension ? 'Pedido de sucursales enviado' : 'Invitación enviada'}
            </Text>
            <Text style={settingsStyles.bannerBody}>
              {success.isExtension
                ? `Le pedimos a ${success.email} que acepte las sucursales nuevas con su cuenta de Vuelto.`
                : `Revisá el mail de ${success.email}. Le mandamos el link para entrar.`}
            </Text>
            {success.inviteUrl ? (
              <>
                <TouchableOpacity
                  style={[settingsStyles.secondaryCta, { marginTop: spacing.sm }]}
                  onPress={async () => {
                    try {
                      await Clipboard.setStringAsync(success.inviteUrl);
                      setCopyFeedback('Link copiado.');
                      setTimeout(() => setCopyFeedback(undefined), 2500);
                    } catch {
                      setCopyFeedback('No se pudo copiar.');
                    }
                  }}
                  accessibilityLabel="Copiar link de invitación"
                  accessibilityRole="button"
                >
                  <Text style={settingsStyles.secondaryCtaText}>Copiar link</Text>
                </TouchableOpacity>
                {copyFeedback ? <Text style={styles.copyFeedback}>{copyFeedback}</Text> : null}
              </>
            ) : null}
            <Text style={styles.successCaption}>Enviada el {formatLocalDateTime(success.invitedAt)}</Text>
          </View>
          <View style={styles.successActions}>
            <TouchableOpacity
              style={settingsStyles.secondaryButton}
              onPress={() => setSuccess(null)}
              accessibilityLabel="Invitar a otro empleado"
              accessibilityRole="button"
            >
              <Text style={settingsStyles.secondaryButtonText}>Invitar a otro</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={settingsStyles.secondaryButton}
              onPress={() => {
                setSuccess(null);
                navigation.navigate('EmployeePermissions', { initialTab: 'invitations' });
              }}
              accessibilityLabel="Ver invitaciones"
              accessibilityRole="button"
              testID="perfil.team.invite.view-invitations"
            >
              <Text style={settingsStyles.secondaryButtonText}>Ver invitaciones</Text>
            </TouchableOpacity>
          </View>
        </View>
      ) : null}

    </ModalScrollScreen>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: ticketColors.bg,
  },
  content: {
    padding: spacing.lg,
    paddingBottom: spacing.xl,
    gap: spacing.lg,
  },
  centered: {
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.lg,
  },
  deniedBox: {
    padding: spacing.xl,
    gap: spacing.sm,
  },
  subheading: {
    ...typography.body,
    color: ticketColors.ink2,
  },
  fieldGroup: {
    gap: spacing.xs,
  },
  roleHint: {
    ...typography.caption,
    color: ticketColors.ink2,
  },
  rolePreviewBox: {
    marginTop: spacing.sm,
    padding: spacing.md,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: ticketColors.border,
    backgroundColor: ticketColors.paper,
    gap: spacing.xs,
  },
  rolePreviewTitle: {
    ...typography.caption,
    fontWeight: '600',
    color: ticketColors.ink,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  rolePreviewCan: {
    ...typography.caption,
    color: ticketColors.ink,
  },
  rolePreviewCannot: {
    ...typography.caption,
    color: ticketColors.ink2,
  },
  emptyBranches: {
    ...typography.caption,
    color: ticketColors.ink3,
  },
  noBranchesMessage: {
    ...typography.caption,
    color: ticketColors.bad,
  },
  error: {
    ...typography.caption,
    color: ticketColors.bad,
  },
  successBlock: {
    gap: spacing.md,
  },
  successActions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  successCaption: {
    ...typography.caption,
    color: ticketColors.ink2,
  },
  copyFeedback: {
    ...typography.caption,
    color: ticketColors.ink2,
    marginTop: spacing.xs,
  },
});
